/**
 * 参数处理工具
 * 用于处理来自第三方APP的URL参数
 */

/**
 * 解析URL查询参数
 * @returns {Object} 解析后的参数对象
 */
export function parseUrlParams() {
  const query = window.location.search.substring(1);
  if (!query) return {};
  
  return query.split('&').reduce((params, param) => {
    const [key, value] = param.split('=');
    if (key && value !== undefined) {
      params[key] = decodeURIComponent(value);
    }
    return params;
  }, {});
}

/**
 * 从路由参数获取ID
 * @returns {string|null} 路由中的ID参数
 */
export function getRouteId() {
  const path = window.location.pathname;
  const pathParts = path.split('/').filter(Boolean);
  
  // 假设ID在路径的最后一部分
  return pathParts.length > 0 ? pathParts[pathParts.length - 1] : null;
}

/**
 * 生成带参数的URL
 * @param {string} path - 基础路径
 * @param {Object} params - 参数对象
 * @returns {string} 完整URL
 */
export function generateUrlWithParams(path, params = {}) {
  const baseUrl = path.startsWith('http') ? path : window.location.origin + path;
  
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
    
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * 处理来自APP的特殊参数格式
 * 有些APP可能使用特殊格式的参数，这里可以进行转换处理
 * @param {Object} params - 原始参数
 * @returns {Object} 处理后的参数
 */
export function processAppParams(params) {
  const processedParams = { ...params };
  
  // 这里可以添加特殊处理逻辑
  // 例如: 某些APP可能将JSON字符串作为参数传递
  if (processedParams.data && typeof processedParams.data === 'string') {
    try {
      processedParams.data = JSON.parse(processedParams.data);
    } catch (e) {
      console.error('解析JSON参数失败:', e);
    }
  }
  
  return processedParams;
}

/**
 * 自动从URL获取并处理所有参数
 * @returns {Object} 处理后的参数对象
 */
export function getAndProcessAllParams() {
  const urlParams = parseUrlParams();
  const routeId = getRouteId();
  
  // 合并参数
  const allParams = { ...urlParams };
  if (routeId) {
    allParams.id = routeId;
  }
  
  return processAppParams(allParams);
}

/**
 * 节点类型映射
 * 将displayType映射到对应的节点类型名称
 */
export const NODE_TYPE_MAP = {
  // 数字类型
  '1': 'common_box',
  '3': 'message_box',
  '2': 'action_box',
  
  // 带引号的数字类型
  "'1'": 'common_box',
  "'2'": 'action_box',
  "'3'": 'message_box',
};

/**
 * 根据displayType获取节点类型
 * @param {string} displayType - 显示类型
 * @returns {string} 节点类型
 */
export function getNodeTypeByDisplayType(displayType) {
  if (!displayType) return 'common_box';
  
  // 直接从映射中获取
  if (NODE_TYPE_MAP[displayType]) {
    return NODE_TYPE_MAP[displayType];
  }
  
  // 如果是 "'1'", "'2'", "'3'" 这种格式，提取数字
  const numMatch = displayType.match(/['"](\d+)['"]/);
  if (numMatch && numMatch[1] && NODE_TYPE_MAP[numMatch[1]]) {
    return NODE_TYPE_MAP[numMatch[1]];
  }
  
  // 默认返回common_box
  console.warn(`未知的displayType值: ${displayType}，使用默认类型common_box`);
  return 'common_box';
}

/**
 * 处理displayType，获取标准化的数字值
 * @param {string} displayType - 原始显示类型
 * @returns {string} 标准化后的数字值
 */
export function processDisplayType(displayType) {
  if (!displayType) return '';
  
  // 如果是 "'1'", "'2'", "'3'" 这种格式，提取数字
  const numMatch = displayType.match(/['"](\d+)['"]/);
  if (numMatch && numMatch[1]) {
    return numMatch[1];
  }
  
  // 如果是中文类型，转换为对应数字
  if (NODE_TYPE_MAP[displayType] && /^\d+$/.test(NODE_TYPE_MAP[displayType])) {
    return NODE_TYPE_MAP[displayType];
  }
  
  // 确保返回的是数字，如果不是数字，则返回默认值"1"
  return /^\d+$/.test(displayType) ? displayType : "1";
} 